import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { getKidSub, useAuthStore } from '@/auth/authStore';
import { readLearningTool } from './studyLearningToolSuggestions';

/** Private, temporary context is carried in memory, not in a public/shareable tool URL. */
export function StudyToolLearningSource() {
  const owner = useAuthStore(() => getKidSub());
  const [params, setParams] = useSearchParams();
  const location = useLocation(),
    key = params.get('learningContext');
  if (!key || !location.pathname.includes('/tools/')) return null;
  const context = owner ? readLearningTool(key, owner, location.pathname) : null;
  return (
    <aside
      className="mb-4 rounded-2xl border border-hairline bg-wash-sky p-4"
      aria-label="Your learning context"
    >
      <div className="flex min-w-0 items-center justify-between gap-2">
        <p className="min-w-0 flex-1 truncate text-sm font-bold text-ink" title={context?.label}>
          {context
            ? `Exploring from: ${context.label}`
            : 'This temporary learning link has expired.'}
        </p>
        <button
          type="button"
          className="btn-pill-ghost min-h-11 shrink-0 text-sm"
          onClick={() =>
            setParams(
              (current) => {
                const next = new URLSearchParams(current);
                next.delete('learningContext');
                return next;
              },
              { replace: true },
            )
          }
        >
          Hide context
        </button>
      </div>
      {context ? (
        <>
          <details className="text-sm text-ink-soft">
            <summary className="min-h-11 cursor-pointer py-3 font-bold">
              See idea
              {context.sourceVersion ? (
                <>
                  <span aria-hidden="true"> · v{context.sourceVersion}</span>
                  <span className="sr-only"> · saved version {context.sourceVersion}</span>
                </>
              ) : (
                ''
              )}
            </summary>
            <p className="whitespace-pre-wrap break-words leading-relaxed">{context.text}</p>
            <p className="my-2 text-xs text-slate2">
              Use the model’s controls to try your idea. Your material was not imported into its
              settings.
            </p>
          </details>
          <Link
            className="btn-pill-secondary inline-flex min-h-11 items-center"
            to={context.returnTo}
          >
            Back to my learning
          </Link>
        </>
      ) : (
        <p className="text-sm text-slate2">
          The tool still works. Return using Study or your browser; private source text is never
          restored from a shared URL.
        </p>
      )}
    </aside>
  );
}
